import { codeSlashOutline, logoGithub, mail, person } from 'ionicons/icons';

import { GH_CLIENT_ID, OAUTH_REDIRECT_URI } from '../constants/secrets';
import { ServiceTypes } from '../constants/service';
import {
  GithubCommit,
  GithubEmail,
  GithubMembership,
  GithubOrganization,
  GithubRepository,
  GithubUser
} from '../types/github.types';
import { Commit, Organization, User } from '../types/services.types';
import { determineDefaultAvatarForUser } from '../util/defaultAvatarUrl';
import { ServiceError } from './errors';
import { get } from './requests';
import { Service } from './service';

export class Github implements Service {
  readonly id = ServiceTypes.GitHub;
  readonly host: string;
  readonly name = 'GitHub';
  readonly isCloud: boolean;

  readonly #apiUrl: string;

  readonly emailsToIgnore = ['@users.noreply.github.com'];
  readonly tokenScopes = ['repo', 'read:user', 'user:email'];
  readonly oauthScopes = ['repo', 'user'];
  readonly patName = 'Personal Access Token';
  readonly placeholderPat = 'ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx';
  readonly patRegex = '^ghp_[a-zA-Z0-9]{36}$';
  readonly patUsername = false;
  readonly patUsernameLink = null;

  readonly orgText = 'organization';
  readonly repoText = 'repository';

  readonly #cloudHost = 'https://github.com';

  readonly cloudAvatarDomains = ['avatars.githubusercontent.com'];

  readonly defaultAvatar = `/img/${this.id}.svg`;

  constructor(host: string) {
    this.host = host;
    this.isCloud = host === this.#cloudHost;

    if (this.isCloud) this.#apiUrl = 'https://api.github.com';
    else this.#apiUrl = `${host}/api/v3`;
  }

  getOAuthLink(state: string, codeChallenge: string): string {
    return `https://github.com/login/oauth/authorize?client_id=${GH_CLIENT_ID}&redirect_uri=${encodeURIComponent(
      OAUTH_REDIRECT_URI
    )}&scope=${encodeURIComponent(this.oauthScopes.join(','))}&state=${state}`;
  }

  getTokenUrl(org: string, ide: string, machine?: string): string {
    let description = `GitLive | ${ide}`;
    if (machine) description += ` | ${machine}`;

    return `${this.host}/settings/tokens/new?description=${encodeURIComponent(description)}&scopes=${encodeURIComponent(this.tokenScopes.join(','))}`;
  }

  async hasAccessToRepoProvided(
    token: string,
    org: string,
    repo: string | null
  ): Promise<boolean> {
    if (!repo) {
      const memberships = await this.#get<GithubMembership[]>(
        token,
        '/user/memberships/orgs'
      );

      if (
        !memberships.some(
          (it) => it.organization.login === org && it.state === 'active'
        )
      ) {
        throw new ServiceError(
          'No Access to Organization',
          `Although you appear to have the correct authorization credentials, the ${org} organization has enabled OAuth App access restrictions, ` +
            'meaning GitLive cannot access private repositories. To request (or grant if you are an admin) access via OAuth, ' +
            'visit: https://github.com/settings/connections/applications/8f74ae0beb7b00957644. ' +
            'Alternatively you can reattempt sign in with a Personal Access Token.'
        );
      }

      return true;
    }

    const result = await fetch(`${this.#apiUrl}/repos/${org}/${repo}`, {
      method: 'get',
      headers: new Headers({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      })
    });

    if (result.status == 404) return false;

    if (!result.ok) {
      const data = await result.json();
      throw new ServiceError('No Access to Organization', data?.message);
    }

    return true;
  }

  async getUser(token: string, org: string): Promise<User> {
    const currentUserResult = await this.#get<GithubUser>(token, '/user');

    return {
      id: `${currentUserResult.id}`,
      photoURL: currentUserResult.avatar_url,
      displayName: currentUserResult.name,
      login: currentUserResult.login,
      email: currentUserResult.email,
      service: this.id
    };
  }

  async getUserEmails(token: string, org: string): Promise<string[]> {
    const userEmailsResult = await this.#get<GithubEmail[]>(
      token,
      '/user/emails'
    );

    return userEmailsResult
      .sort((a, b) => (a.primary ? -1 : 1))
      .map((it) => it.email);
  }

  async getOrganization(token: string, org: string): Promise<Organization> {
    try {
      const orgResult = await this.#get<GithubOrganization>(
        token,
        `/orgs/${org}`
      );

      return {
        id: `${orgResult.id}`,
        photoURL: orgResult.avatar_url || this.defaultAvatar,
        displayName: orgResult.login,
        type: orgResult.type,
        createdAt: orgResult.created_at,
        enterprise:
          orgResult.plan?.name === 'enterprise' ||
          orgResult.members_can_create_internal_repositories
      };
    } catch (error: any) {
      const userResult = await this.#get<GithubUser>(token, `/users/${org}`);

      return {
        id: `${userResult.id}`,
        photoURL: userResult.avatar_url || this.defaultAvatar,
        displayName: userResult.login,
        type: userResult.type,
        createdAt: userResult.created_at,
        enterprise: false
      };
    }
  }

  async getMostRecentRepos(token: string, org: string): Promise<string[]> {
    const currentUserResult = await this.#get<GithubUser>(
      token,
      `/users/${org}`
    );
    let lastUpdatedRepos: GithubRepository[] = [];
    if (currentUserResult.type === 'User') {
      lastUpdatedRepos = await this.#get<GithubRepository[]>(
        token,
        `/users/${org}/repos?visibility=private&sort=pushed&affiliation=owner&per_page=5&page=1`
      );
      if (lastUpdatedRepos.length === 0)
        lastUpdatedRepos = await this.#get<GithubRepository[]>(
          token,
          `/users/${org}/repos?visibility=public&sort=pushed&affiliation=owner&per_page=5&page=1`
        );
    } else {
      lastUpdatedRepos = await this.#get<GithubRepository[]>(
        token,
        `/orgs/${org}/repos?type=private&sort=pushed&per_page=5&page=1`
      );
      if (lastUpdatedRepos.length === 0)
        lastUpdatedRepos = await this.#get<GithubRepository[]>(
          token,
          `/orgs/${org}/repos?type=public&sort=pushed&per_page=5&page=1`
        );
    }

    return lastUpdatedRepos.map((it) => it.name);
  }

  async getLatestCommitsFromRepos(
    token: string,
    org: string,
    repos: string[]
  ): Promise<Commit[][]> {
    try {
      return await Promise.all(
        repos.map(async (repo) => {
          try {
            const latestCommits = await this.#get<GithubCommit[]>(
              token,
              `/repos/${org}/${repo}/commits?per_page=100&page=1`
            );

            return latestCommits.map(({ author, commit }) => ({
              email: commit.author.email,
              name: commit.author.name,
              photoURL:
                author?.avatar_url ||
                determineDefaultAvatarForUser(
                  commit.author.name,
                  commit.author.email
                )
            }));
          } catch (error: any) {
            console.warn(`Failed to fetch commits for ${org}/${repo}:`, error);

            return [];
          }
        })
      );
    } catch (error: any) {
      if (error.message.includes('Git Repository is empty.')) return [];
      throw error;
    }
  }

  async getAvatar(
    token: string,
    email: string,
    placeholder: string,
    commitUrl: string
  ): Promise<string> {
    return placeholder;
  }

  getNonCloudAuthenticatedUrl(url: string): { url: string; useAuth: boolean } {
    const imageUrl = new URL(url);
    const hostUrl = new URL(this.host);

    if (
      imageUrl.hostname !== this.#cloudHost &&
      !this.cloudAvatarDomains.includes(imageUrl.hostname)
    ) {
      imageUrl.pathname = `/api/v3/enterprise${imageUrl.pathname}`;
    }

    return {
      useAuth: imageUrl.hostname === hostUrl.hostname,
      url: imageUrl.toString()
    };
  }

  getAuthorizationHeader(token: string): string {
    return `Bearer ${token}`;
  }

  readonly oauthPermissions = [
    {
      id: 'github_requests',
      icon: logoGithub,
      title: 'Requested OAuth scopes',
      subtitle:
        'When you authorize GitLive on your GitHub account we request permission to the following resources'
    },
    {
      id: 'scopes',
      icon: '',
      scopes: [
        {
          id: 'repo',
          icon: codeSlashOutline,
          title: 'Repositories',
          subtitle: 'Public and private',
          descriptionHTML:
            'Repository data is accessed via the API to show the latest changes from your remote repository without you having to perform git fetch locally.' +
            '<p style="padding-top: 4px">It is also used to display branches from private repositories in the organization view.</p>'
        },
        {
          id: 'user_data',
          icon: person,
          title: 'Personal user data',
          subtitle: 'Full access',
          descriptionHTML:
            'Your email addresses are used to link your account with commit data from your git repository, and your primary email address is used to identify and contact you.' +
            '<p style="padding-top: 4px">Additionally, your name and avatar is used to display to the other members of your organization.</p>'
        }
      ]
    }
  ];

  readonly tokenPermissions = [
    {
      id: 'github_requests',
      icon: logoGithub,
      title: 'Required scopes',
      subtitle:
        'The following scopes are required when creating the personal access token'
    },
    {
      id: 'scopes',
      icon: '',
      scopes: [
        {
          id: 'repo',
          icon: codeSlashOutline,
          title: 'repo',
          subtitle: 'Full control of private repositories',
          descriptionHTML:
            'Repository data is accessed via the API to show the latest changes from your remote repository without you having to perform git fetch locally.' +
            '<p style="padding-top: 4px">It is also used to display branches from private repositories in the organization view.</p>'
        },
        {
          id: 'read_user',
          icon: person,
          title: 'read:user',
          subtitle: 'Read all user profile data',
          descriptionHTML:
            'Your name and avatar are used to display to the other members of your organization.'
        },
        {
          id: 'user_email',
          icon: mail,
          title: 'user:email',
          subtitle: 'Access user email addresses (read-only)',
          descriptionHTML:
            'Your email addresses are used to link your account with commit data from your git repository, and your primary email address is used to identify and contact you.'
        }
      ]
    }
  ];

  async #get<T>(token: string, uri: string): Promise<T> {
    return await get(this.#apiUrl, token, uri);
  }
}
